"use client";

import { formatPounds } from "@/lib/deposit-display";
import { formatNightly, formatStayBreakdown, type StayQuote } from "@/lib/stay-quote";

/**
 * Price block at the top of the property booking form: the nightly price, then (once dates are
 * chosen) the server's night by night breakdown, total and deposit. Amounts come from the stay
 * quote only, never worked out here.
 */
export function PropertyStayPrice({
  priceCents,
  fromNightlyCents,
  hasDates,
  quote,
  loading,
  error,
  depositCents,
}: {
  priceCents: number;
  fromNightlyCents?: number | null;
  /** Valid check-in and check-out are chosen */
  hasDates: boolean;
  quote: StayQuote | null;
  loading: boolean;
  error: string | null;
  /** Deposit to show (already filtered by displayDepositCents against the stay total), or null */
  depositCents: number | null;
}) {
  const showFrom = fromNightlyCents != null && fromNightlyCents < priceCents;
  return (
    <div data-testid="property-stay-price">
      <p className="text-2xl font-semibold text-foreground">
        {showFrom && <span className="text-base font-normal text-muted-foreground">from </span>}
        {formatNightly(showFrom ? fromNightlyCents! : priceCents)}
        <span className="text-base font-normal text-muted-foreground"> per night</span>
      </p>
      {showFrom && !hasDates && (
        <p className="mt-1 text-sm text-muted-foreground">Prices vary by season. Choose your dates to see the total.</p>
      )}
      {hasDates && loading && (
        <p className="mt-2 text-sm text-muted-foreground" aria-live="polite">
          Working out the price…
        </p>
      )}
      {hasDates && !loading && error && (
        <p className="mt-2 text-sm text-destructive" role="alert">
          {error}
        </p>
      )}
      {hasDates && !loading && quote && (
        <div className="mt-2 space-y-1">
          <p className="text-sm text-muted-foreground">
            {quote.nights} night{quote.nights !== 1 ? "s" : ""}: <span data-testid="stay-breakdown">{formatStayBreakdown(quote.breakdown)}</span>
          </p>
          <p className="text-lg font-semibold text-foreground" data-testid="stay-total">
            Total: {formatPounds(quote.totalCents)}
          </p>
          {depositCents != null && (
            <p className="text-sm font-medium text-foreground">
              Deposit: {formatPounds(depositCents)}
              {quote.totalCents > depositCents && (
                <span className="text-muted-foreground font-normal">
                  {" "}(remaining {formatPounds(quote.totalCents - depositCents)} due later)
                </span>
              )}
            </p>
          )}
          {quote.minStayError && (
            <p className="text-sm text-destructive" role="alert" data-testid="stay-min-error">
              {quote.minStayError}
            </p>
          )}
        </div>
      )}
      {!hasDates && depositCents != null && (
        <p className="mt-2 text-sm font-medium text-foreground">Deposit: {formatPounds(depositCents)}</p>
      )}
    </div>
  );
}
